import type { FormEvent } from 'react';
import React from 'react';

import { Flex, FormField, FormHelp, Grid, Input, Label, Paragraph, Textarea, TextButton } from '@zillow/constellation';

import type Field from '../../../../models/Field';
import { PERSON } from '../../../../constants/index';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';

type Props = {
  index: number;
  onFieldChange: (event: FormEvent<HTMLElement>) => void;
  onRemovePerson: (event: FormEvent<HTMLElement>) => void;
  firstNameField: Field;
  firstNameValue: any;
  lastNameField: Field;
  lastNameValue: any;
  phoneNumberField: Field;
  phoneNumberValue: any;
  emailField: Field;
  emailValue: any;
  mailingAddressField?: Field;
  mailingAddressValue?: any;
  personTypeLabel: string;
  addressLabel: string;
  className: string;
};

PersonInformation.defaultProps = {
  addressLabel: PERSON.LABEL.MAILING_ADDRESS,
};

export default function PersonInformation({
  index,
  firstNameField,
  firstNameValue,
  lastNameField,
  lastNameValue,
  phoneNumberField,
  phoneNumberValue,
  emailField,
  emailValue,
  mailingAddressField,
  mailingAddressValue,
  onFieldChange,
  onRemovePerson,
  personTypeLabel,
  addressLabel,
}: Props): JSX.Element {
  return (
    <Grid key={index} className={FULLSTORY_EXCLUDE} display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="xs">
      {index > 0 && (
        <Grid gridColumnEnd="span 12">
          <Flex display="flex" justifyContent="space-between" alignItems="center">
            <Paragraph fontType="bodyHeading">
              {personTypeLabel} #{index + 1}
            </Paragraph>
            <TextButton data-test="remove-person" data-index={index} onClick={onRemovePerson}>
              Delete
            </TextButton>
          </Flex>
        </Grid>
      )}
      <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
        <FormField
          error={firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME) ? true : false}
          label={<Label id={`${personTypeLabel}#${index + 1}-first-name`}>{PERSON.LABEL.FIRST_NAME}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-first-name`}
              data-field-name={firstNameField.fieldName}
              type="fname"
              value={firstNameValue}
              onChange={onFieldChange}
            />
          }
          description={
            firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME) && (
              <FormHelp>{firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME)}</FormHelp>
            )
          }
        />
      </Grid>
      <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
        <FormField
          error={lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME) ? true : false}
          label={<Label id={`${personTypeLabel}#${index + 1}-last-name`}>{PERSON.LABEL.LAST_NAME}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-last-name`}
              data-field-name={lastNameField.fieldName}
              type="lname"
              value={lastNameValue}
              onChange={onFieldChange}
            />
          }
          description={
            lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME) && (
              <FormHelp>{lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME)}</FormHelp>
            )
          }
        />
      </Grid>
      {mailingAddressField && (
        <Grid gridColumnEnd="span 12">
          <FormField
            marginBottom="xs"
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label id={`${personTypeLabel}#${index + 1}-mailing-address`}>{addressLabel}</Label>}
            control={
              <Textarea
                aria-labelledby={`${personTypeLabel}#${index + 1}-mailing-address`}
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                resize="vertical"
                rows="3"
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              )
            }
          />
        </Grid>
      )}
      <Grid gridColumnEnd="span 12">
        <FormField
          marginBottom="xs"
          error={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            )
              ? true
              : false
          }
          label={<Label id={`${personTypeLabel}#${index + 1}-email`}>{PERSON.LABEL.EMAIL}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-email`}
              data-field-name={emailField.fieldName}
              value={emailValue}
              onChange={onFieldChange}
              // type='email' Disabled due to inconsistent/unpredicatable support across browsers
            />
          }
          description={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            ) && (
              <FormHelp>
                {emailField.showOnError(
                  emailValue.includes(' ')
                    ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE
                    : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
                )}
              </FormHelp>
            )
          }
        />
      </Grid>
      {phoneNumberField && (
        <Grid gridColumnEnd="span 12">
          <FormField
            error={phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER) ? true : false}
            label={<Label id={`${personTypeLabel}#${index + 1}-phone-number`}>{PERSON.LABEL.PHONE_NUMBER}</Label>}
            control={
              <Input
                aria-labelledby={`${personTypeLabel}#${index + 1}-phone-number`}
                data-field-name={phoneNumberField.fieldName}
                value={phoneNumberValue}
                onChange={onFieldChange}
                type="tel"
              />
            }
            description={
              phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER) && (
                <FormHelp>{phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER)}</FormHelp>
              )
            }
          />
        </Grid>
      )}
    </Grid>
  );
}
